import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowLeft, RefreshCw, FileText, BarChart, Loader, AlertOctagon, AlertTriangle,
  Zap, Minus, TrendingUp, TrendingDown, X, HardHat, CheckCircle, Gem, ShieldCheck
} from 'lucide-react';
import RStrategyTimeline from './RStrategyTimeline';
import TriageResultCard from '../../components/TriageResultCard';
import { getMarketTrends, getMaterialMarketData, getStrategies, analyzeAsset, suggestStrategy, getAssetPassport, generateAssetPassport, getAssetMci, estimateLCA, getRepairability, getRUL, checkSVHCCompliance, updateAsset, estimateMaterials } from '../../services/api';
import { CRITICAL_MATERIALS, normalizeMaterialName } from '../../constants/materials';

// Match App.js getConditionIcon
const getConditionIcon = (rating) => {
  if (rating >= 4.5) return <Gem className="w-4 h-4 text-cyan-500" />;
  if (rating >= 3.0) return <ShieldCheck className="w-4 h-4 text-green-500" />;
  if (rating >= 1.5) return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
  return <AlertTriangle className="w-4 h-4 text-red-500" />;
};

const AssetTriagePage = ({ asset, onBack, onSaveStrategy, onSaveAIRecommendations, showMessage, isLoading = false }) => {
  // State for dynamic data from API
  const [materialMarketData, setMaterialMarketData] = useState({});
  const [enhancedRStrategies, setEnhancedRStrategies] = useState({});

  // Safe asset with defaults to prevent undefined errors
  const safeAsset = useMemo(() => {
    if (!asset) return null;
    return {
      id: asset.id || '',
      name: asset.name || 'Unknown Asset',
      description: asset.description || 'No description available',
      conditionRating: Number(asset.condition_rating) || 3.0,
      circularityScore: Number(asset.circularity_score) || 70.0,
      initialValue: Number(asset.initial_value) || 0,
      currentValue: Number(asset.current_market_value) || 0,
      marketValue: Number(asset.current_market_value) || 0,
      material_components: Array.isArray(asset.material_components) ? asset.material_components : ['Aluminum', 'Steel'],
      weight: Number(asset.weight) || 10,
      assetType: asset.asset_type,
      currentStrategy: asset.current_strategy,
      ageMonths: asset.age_in_months,
      lifeExpectancyMonths: asset.life_expectancy_in_months,
      lastService: asset.last_service,
      // components: asset.components || []
    };
  }, [asset]);

  useEffect(() => {
    const loadMarketData = async () => {
      try {
        const marketData = await getMaterialMarketData();
        setMaterialMarketData(marketData);
      } catch (error) {
        console.error('Error loading material market data:', error);
      }
    };
    const loadStrategies = async () => {
      try {
        const strategies = await getStrategies();
        setEnhancedRStrategies(strategies);
      } catch (error) {
        console.error('Error loading strategies:', error);
      }
    };
    loadMarketData();
    loadStrategies();
  }, []);


  const [triageResult, setTriageResult] = useState(null);
  const [riskResult, setRiskResult] = useState(null);
  const [isLoadingTriage, setIsLoadingTriage] = useState(false);
  const [isLoadingRisk, setIsLoadingRisk] = useState(false);
  const [showSimulationModal, setShowSimulationModal] = useState(false);
  const [showRiskSimulationModal, setShowRiskSimulationModal] = useState(false);
  const [simulationResults, setSimulationResults] = useState(null);

  const [riskInputs, setRiskInputs] = useState({
    financialCostPercentage: 10,
    days: 30,
    relativeCostChange: 'stable'
  });

  const [simulationInputs, setSimulationInputs] = useState({
    financialCostPercentage: 10,
    days: 90,
    relativeCostChange: 'stable',
    repairCost: 0,
    downtimeDays: 7,
    materialComposition: {}
  });

  // PDF export helpers (hooks hoisted to top to satisfy React hooks rules)
  const exportRef = useRef(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Advanced circularity states (moved above to avoid TDZ with parsedMaterialsForAI)
  const [passport, setPassport] = useState(null);
  const [isLoadingPassport, setIsLoadingPassport] = useState(false);
  const [passportError, setPassportError] = useState(null);
  const [isGeneratingPassport, setIsGeneratingPassport] = useState(false);

  // Local override for materials (used after on-page AI re-estimation) — moved up to avoid TDZ
  const [materialsOverride, setMaterialsOverride] = useState(null);

  // Parsed materials for AI/fallback display (prefer local override → passport → string parsing)
  const parsedMaterialsForAI = useMemo(() => {
    if (Array.isArray(materialsOverride) && materialsOverride.length > 0) {
      return materialsOverride.map(m => ({ name: m.name, percentage: Number(m.percentage || 0) }));
    }
    // Prefer passport.materials
    if (passport && Array.isArray(passport.materials) && passport.materials.length > 0) {
      return passport.materials.map(m => ({ name: m.name, percentage: Number(m.percentage || 0) }));
    }
    // Fallback: parse asset.material_components string like "Steel50,Aluminum20"
    const raw = asset?.material_components || '';
    if (typeof raw === 'string' && raw.length > 0) {
      return raw.split(',').map(s => s.trim()).filter(Boolean).map(part => {
        const name = part.replace(/\d+/g, '').trim();
        const digits = part.match(/\d+/g);
        const pct = digits ? Number(digits.join('')) : 0;
        return { name: name || part, percentage: pct };
      });
    }
    // Last resort: array on safeAsset
    if (Array.isArray(safeAsset?.material_components)) {
      return safeAsset.material_components.map(n => ({ name: String(n), percentage: 0 }));
    }
    return [];
  }, [materialsOverride, passport, asset?.material_components, safeAsset?.material_components]);

  // Derived material breakdown with market data and critical flags
  const materialBreakdown = useMemo(() => {
    if (!safeAsset) return [];
    const list = Array.isArray(parsedMaterialsForAI) ? parsedMaterialsForAI : [];
    return list.map((m) => {
      const name = normalizeMaterialName(m.name);
      const pct = Math.max(0, Math.min(100, Number(m.percentage) || 0));
      const md = materialMarketData?.[name] || {};
      const currentPrice = Number(md.currentPrice || 0);
      const trend = md.trend || 'stable';
      const volatility = Number(md.volatility || 0);
      const isCritical = CRITICAL_MATERIALS.has(name);
      const value = (pct / 100) * (Number(safeAsset.weight || 0)) * currentPrice;
      return { name, percentage: pct, currentPrice, trend, volatility, isCritical, value: Number.isFinite(value) ? Number(value.toFixed(2)) : 0 };
    });
  }, [parsedMaterialsForAI, safeAsset?.weight, materialMarketData]);


  const [isEstimatingMaterials, setIsEstimatingMaterials] = useState(false);
  const handleReestimateMaterials = useCallback(async () => {
    if (!safeAsset) return;
    try {
      setIsEstimatingMaterials(true);
      const hints = { asset_type: safeAsset.assetType, brand: asset?.brand, model_name: asset?.model_name, known_materials: parsedMaterialsForAI.map(m => m.name) };
      const res = await estimateMaterials(safeAsset.id, hints);
      if (res && Array.isArray(res.compositions) && res.compositions.length > 0) {
        const comps = res.compositions.map(c => ({ name: normalizeMaterialName(c.name), percentage: Math.max(0, Math.min(100, Number(c.percentage)||0)) }));
        setMaterialsOverride(comps);
        // Persist to backend as string mirror
        const matString = comps.map(m => `${normalizeMaterialName(m.name)}${Math.round(Number(m.percentage||0))}`).join(',');
        try { await updateAsset(safeAsset.id, { material_components: matString }); } catch(e) { /* non-blocking */ }
        // Optionally trigger backend AI update
        try { await analyzeAsset(safeAsset.id); await suggestStrategy(safeAsset.id); } catch(e) { /* best-effort */ }
        showMessage && showMessage('Materials re-estimated with AI.');
      }
    } catch (e) {
      console.error('Re-estimate materials failed', e);
      showMessage && showMessage('Failed to re-estimate materials.');
    } finally {
      setIsEstimatingMaterials(false);
    }
  }, [safeAsset, asset?.brand, asset?.model_name, parsedMaterialsForAI, updateAsset, analyzeAsset, suggestStrategy, showMessage]);

  const handleRefreshMarket = useCallback(async () => {
    try {
      const data = await getMaterialMarketData();
      setMaterialMarketData(data);
      showMessage && showMessage('Market data refreshed.');
    } catch (e) {
      console.error('Refresh market data failed', e);
    }
  }, [showMessage]);

  const handleRiskInputChange = (e) => {
    const { name, value } = e.target;
    setRiskInputs(prev => ({ ...prev, [name]: name === 'financialCostPercentage' || name === 'days' ? parseFloat(value) : value }));
  };

  const calculateRiskLevel = (strategyKey) => {
    const riskFactors = { 'R5': 0.2, 'R4': 0.4, 'R3': 0.6, 'R2': 0.8, 'R1': 0.9 };
    return riskFactors[strategyKey] || 0.5;
  };

  const runTriage = useCallback(() => {
    if (!safeAsset) return;
    setIsLoadingTriage(true);
    setTriageResult(null);
    setTimeout(() => {
      const { conditionRating, circularityScore } = safeAsset;
      let suggestedStrategy;
      let rationale;
      if (conditionRating >= 4.0 && circularityScore >= 80) { suggestedStrategy = 'R7'; rationale = "High condition and excellent circularity potential justify maximizing asset life through Re-use or Repair (R7)."; }
      else if (conditionRating >= 2.5 && circularityScore >= 60) { suggestedStrategy = 'R6'; rationale = "Moderate condition but good circularity score suggest a Remanufacturing or Refurbishment (R6) approach to restore high value."; }
      else if (conditionRating >= 1.5 && circularityScore >= 40) { suggestedStrategy = 'R5'; rationale = "Fair condition with moderate circularity potential means Repurposing (R5) is the most viable option to extract residual value."; }
      else { suggestedStrategy = 'R4'; rationale = "Low condition and poor circularity score indicate that a full Recycle (R4) is the most economically and environmentally responsible strategy remaining."; }
      setTriageResult({ suggestedStrategy, rationale });
      setIsLoadingTriage(false);
      showMessage && showMessage('Strategy Triage run complete. Suggested strategy is ready.');
    }, 2000);
  }, [safeAsset, showMessage]);


  const runRiskSimulation = useCallback(() => {
    if (!safeAsset) return;
    setIsLoadingRisk(true);
    setRiskResult(null);
    setTimeout(() => {
      const { financialCostPercentage, days, relativeCostChange } = riskInputs;
      let baseRisk = 0;
      baseRisk += (5 - safeAsset.conditionRating) * 8;
      baseRisk += (safeAsset.marketValue / safeAsset.initialValue) * 20;
      baseRisk += parseFloat(financialCostPercentage) * 1;
      baseRisk += (parseFloat(days) / 180) * 10;
      if (relativeCostChange === 'increase') baseRisk += 15; else if (relativeCostChange === 'decrease') baseRisk -= 5;
      let finalRiskScore = Math.min(100, Math.max(0, baseRisk));
      let riskLevel, colorClass, badgeClass;
      if (finalRiskScore > 75) { riskLevel = 'Extreme'; colorClass = 'text-red-600'; badgeClass = 'bg-red-600'; }
      else if (finalRiskScore > 50) { riskLevel = 'High'; colorClass = 'text-orange-600'; badgeClass = 'bg-orange-600'; }
      else if (finalRiskScore > 25) { riskLevel = 'Medium'; colorClass = 'text-yellow-600'; badgeClass = 'bg-yellow-600'; }
      else { riskLevel = 'Low'; colorClass = 'text-green-600'; badgeClass = 'bg-green-600'; }
      setRiskResult({ score: finalRiskScore.toFixed(0), level: riskLevel, colorClass, badgeClass, analysis: `The ${riskLevel} risk level is driven primarily by the asset's current condition (${safeAsset.conditionRating.toFixed(1)}/5.0) and the projected ${financialCostPercentage}% cost impact over ${days} days. The assumed relative cost change is ${relativeCostChange}.` });
      setIsLoadingRisk(false);
      showMessage && showMessage('Risk simulation complete.');
    }, 2000);
  }, [safeAsset, riskInputs, showMessage]);

  const simulateGeminiTriage = useCallback(() => { runTriage(); }, [runTriage]);

  const timelineAsset = useMemo(() => {
    const key = triageResult?.suggestedStrategy;
    if (!key) return {};
    const time = enhancedRStrategies[key]?.timeToImplement || 14;
    const prep = Math.max(1, Math.round(time * 0.2));
    const exec = Math.max(1, Math.round(time * 0.6));
    const qa = Math.max(0, time - prep - exec);
    return { estimatedTriageTimeline: { [key]: [ { step: 'Preparation', durationDays: prep, risk: 'Low' }, { step: 'Execution', durationDays: exec, risk: 'Medium' }, { step: 'Quality Assurance', durationDays: qa, risk: 'Low' }, { step: 'Strategy Complete', durationDays: 0, risk: 'N/A' } ] } };
  }, [triageResult, enhancedRStrategies]);


  const [marketTrends, setMarketTrends] = useState(null);
  const [isLoadingTrends, setIsLoadingTrends] = useState(false);
  const [trendsError, setTrendsError] = useState(null);

  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiStrategyRecommendations, setAiStrategyRecommendations] = useState(null);


  const [mci, setMci] = useState(null);
  const [isLoadingMci, setIsLoadingMci] = useState(false);
  const [mciError, setMciError] = useState(null);

  const [lca, setLca] = useState(null);
  const [isLoadingLca, setIsLoadingLca] = useState(false);
  const [lcaError, setLcaError] = useState(null);

  const [repairability, setRepairability] = useState(null);
  const [isLoadingRepairability, setIsLoadingRepairability] = useState(false);
  const [repairabilityError, setRepairabilityError] = useState(null);

  const [rul, setRul] = useState(null);
  const [isLoadingRul, setIsLoadingRul] = useState(false);
  const [rulError, setRulError] = useState(null);

  const [complianceFlags, setComplianceFlags] = useState([]);
  const [isLoadingCompliance, setIsLoadingCompliance] = useState(false);
  const [complianceError, setComplianceError] = useState(null);


  // Detect when backend returned a heuristic fallback (no AI) to surface a badge
  const isFallbackAnalysis = useMemo(() => {
    const reason = aiAnalysis?.reasoning || '';
    return /heuristic/i.test(reason) || aiSuggestions?.isEstimate === true;
  }, [aiAnalysis, aiSuggestions]);

  const estimateAssetValues = useCallback((asset, trends, suggestions) => {
    if (!asset) return {};
    const baseValue = asset.currentValue || 0;
    const conditionFactor = (asset.conditionRating / 5) || 0.5;
    const materialAnalysis = asset.materials?.map(mat => {
      const matData = materialMarketData[mat] || {};
      const currentPrice = matData.currentPrice || 0;
      const trend = matData.trend || 'stable';
      const volatility = matData.volatility || 0.1;
      const trendImpact = trend === 'up' ? 1.05 : trend === 'down' ? 0.95 : 1.0;
      const adjustedPrice = currentPrice * trendImpact * (1 + (Math.random() * volatility - volatility/2));
      const materialWeight = (asset.weight || 10) * (1 / (asset.materials?.length || 1));
      const materialValue = materialWeight * adjustedPrice;
      return { name: mat, weight: materialWeight, price: adjustedPrice, value: materialValue, trend, volatility };
    }) || [];
    const totalMaterialValue = materialAnalysis.reduce((sum, mat) => sum + mat.value, 0);
    const baseRecoveryRate = Math.min(0.95, 0.5 + (conditionFactor * 0.5));
    const materialRecoveryBonus = materialAnalysis.length > 0 ? (materialAnalysis.reduce((sum, mat) => sum + (mat.trend === 'up' ? 0.05 : mat.trend === 'down' ? -0.03 : 0), 0) / materialAnalysis.length) : 0;
    const recoveryRate = Math.max(0.1, Math.min(0.99, (suggestions?.suggested_recovery_rate || baseRecoveryRate) + materialRecoveryBonus));
    const baseCostFactor = 0.2 - (conditionFactor * 0.1) + (Math.random() * 0.1);
    const materialCostImpact = materialAnalysis.length > 0 ? materialAnalysis.reduce((sum, mat) => sum + (mat.trend === 'up' ? 0.02 : mat.trend === 'down' ? -0.01 : 0), 0) : 0;
    const costFactor = Math.max(0.05, Math.min(0.8, (suggestions?.suggested_cost_factor || baseCostFactor) + materialCostImpact));
    const recoveryValue = (baseValue * recoveryRate) + (totalMaterialValue * 0.7);
    const recoveryCost = baseValue * costFactor;
    const netValue = recoveryValue - recoveryCost;
    const roi = recoveryCost > 0 ? (netValue / recoveryCost) * 100 : 0;
    const paybackMonths = netValue > 0 ? Math.max(1, Math.min(60, (recoveryCost / (netValue / 12)))) : null;
    const baseConfidence = 0.8;
    const conditionConfidence = 0.9 - (Math.abs(0.5 - conditionFactor) * 0.4);
    const materialConfidence = materialAnalysis.length > 0 ? 0.8 + (materialAnalysis.reduce((sum, mat) => sum + (1 - mat.volatility), 0) / materialAnalysis.length * 0.2) : 0.7;
    const confidence = Math.min(0.99, Math.max(0.7, (baseConfidence * 0.4) + (conditionConfidence * 0.3) + (materialConfidence * 0.3)));
    return { recoveryRate: parseFloat(recoveryRate.toFixed(3)), costFactor: parseFloat(costFactor.toFixed(3)), recoveryValue: parseFloat(recoveryValue.toFixed(2)), recoveryCost: parseFloat(recoveryCost.toFixed(2)), netValue: parseFloat(netValue.toFixed(2)), roi: parseFloat(roi.toFixed(1)), paybackMonths: paybackMonths ? parseFloat(paybackMonths.toFixed(1)) : null, materialValue: parseFloat(totalMaterialValue.toFixed(2)), lastUpdated: new Date().toISOString(), confidence: parseFloat(confidence.toFixed(2)), materialAnalysis };
  }, []);

  const fetchAiInsights = useCallback(async (forceRefresh = false) => {
    if (!safeAsset) return null;
    if (isLoadingAi && !forceRefresh) return null;
    setIsLoadingAi(true);
    setAiSuggestions(prev => ({ ...prev, isLoading: true, lastUpdated: new Date().toISOString(), error: null }));

    try {
      // Get AI analysis from backend
      const analysisResponse = await analyzeAsset(safeAsset.id);
      setAiAnalysis(analysisResponse);

      // Get AI strategy recommendations from backend
      const strategyResponse = await suggestStrategy(safeAsset.id);
      setAiStrategyRecommendations(strategyResponse);

      // Update local suggestions with backend data
      const newAiSuggestions = {
        ...analysisResponse,
        ...strategyResponse,
        isLoading: false,
        lastFetched: new Date().toISOString(),
        isEstimate: false
      };
      setAiSuggestions(newAiSuggestions);


      return newAiSuggestions;
    } catch (error) {
      console.error('Backend AI analysis failed:', error);
      // Fallback to local estimation
      const estimates = estimateAssetValues(safeAsset, null, null);
      const fallbackSuggestions = {
        ...estimates,
        isLoading: false,
        isEstimate: true,
        lastFetched: new Date().toISOString(),
        confidence: Math.max(0.6, estimates.confidence || 0.7),
        error: `Backend AI unavailable - ${error?.message || 'Network error'}`,
        warning: 'Results may be less accurate without server-side analysis'
      };
      setAiSuggestions(fallbackSuggestions);
      return fallbackSuggestions;
    } finally {
      setIsLoadingAi(false);
    }
  }, [safeAsset, isLoadingAi, estimateAssetValues, showMessage]);

  // Disable legacy auto-fetch of backend AI on mount to honor Option 1+2 (frontend-only narrative)
  useEffect(() => {
    // Intentionally left blank; we only generate narrative after user clicks Run Triage
  }, []);

  // After triage completes, fetch market trends (read-only) and generate frontend AI narrative (no backend writes)
  useEffect(() => {
    if (!triageResult || !safeAsset) return;
    setIsLoadingTrends(true);
    setTrendsError(null);
    getMarketTrends({ assetId: safeAsset.id })
      .then((data) => setMarketTrends(data))
      .catch((err) => {
        console.error('Failed to load market trends', err);
        setTrendsError(err?.message || 'Failed to load market trends');
      })
      .finally(() => {
        setIsLoadingTrends(false);
      });
  }, [triageResult, safeAsset, fetchAiInsights]);

  // Load advanced circularity panels when asset changes
  useEffect(() => {
    if (!safeAsset) return;
    let cancelled = false;

    setIsLoadingPassport(true); setPassportError(null);
    setIsLoadingMci(true); setMciError(null);
    setIsLoadingLca(true); setLcaError(null);
    setIsLoadingRepairability(true); setRepairabilityError(null);
    setIsLoadingRul(true); setRulError(null);

    Promise.allSettled([
      getAssetPassport(safeAsset.id),
      getAssetMci(safeAsset.id),
      estimateLCA({ assetId: safeAsset.id }),
      getRepairability(safeAsset.id),
      getRUL(safeAsset.id),
    ]).then((results) => {
      if (cancelled) return;
      const [p, m, l, r, ru] = results;
      if (p.status === 'fulfilled') setPassport(p.value); else setPassportError(p.reason?.message || 'Failed to load passport');
      if (m.status === 'fulfilled') setMci(m.value); else setMciError(m.reason?.message || 'Failed to load MCI');
      if (l.status === 'fulfilled') setLca(l.value); else setLcaError(l.reason?.message || 'Failed to load LCA');
      if (r.status === 'fulfilled') setRepairability(r.value); else setRepairabilityError(r.reason?.message || 'Failed to load repairability');
      if (ru.status === 'fulfilled') setRul(ru.value); else setRulError(ru.reason?.message || 'Failed to load RUL');
    }).finally(() => {
      if (cancelled) return;
      setIsLoadingPassport(false);
      setIsLoadingMci(false);
      setIsLoadingLca(false);
      setIsLoadingRepairability(false);
      setIsLoadingRul(false);
    });

    return () => { cancelled = true; };
  }, [safeAsset?.id]);

  // Compliance check after passport/materials become available
  useEffect(() => {
    if (!safeAsset) return;

    const parseMaterials = () => {
      // Prefer passport.materials [{name, percentage}]
      if (passport && Array.isArray(passport.materials) && passport.materials.length > 0) {
        return passport.materials.map(m => ({ name: m.name, percentage: Number(m.percentage || 0) }));
      }
      // Fallback: try to parse asset.material_components string like "Steel50,Aluminum20"
      const raw = asset?.material_components || '';
      if (typeof raw === 'string' && raw.length > 0) {
        return raw.split(',').map(s => s.trim()).filter(Boolean).map(part => {
          const name = part.replace(/\d+/g, '').trim();
          const digits = part.match(/\d+/g);
          const pct = digits ? Number(digits.join('')) : 0;
          return { name: name || part, percentage: pct };
        });
      }
      // Last resort: use any array set on safeAsset
      if (Array.isArray(safeAsset.material_components)) {
        return safeAsset.material_components.map(n => ({ name: String(n), percentage: 0 }));
      }
      return [];
    };

    const mats = parseMaterials();
    if (!mats.length) { setComplianceFlags([]); return; }

    let cancelled = false;
    setIsLoadingCompliance(true); setComplianceError(null);
    checkSVHCCompliance(mats)
      .then((res) => { if (!cancelled) setComplianceFlags(res.flags || []); })
      .catch((err) => { if (!cancelled) setComplianceError(err?.message || 'Compliance check failed'); })
      .finally(() => { if (!cancelled) setIsLoadingCompliance(false); });

    return () => { cancelled = true; };
  }, [passport, safeAsset?.id]);

  const handleRefreshAiInsights = useCallback(async () => {
    if (isLoadingAi) return;
    showMessage && showMessage('Refreshing AI estimates...');
    const result = await fetchAiInsights(true);
    if (result?.error) {
      showMessage && showMessage(`Warning: ${result.error}`, 'warning');
    } else {
      showMessage && showMessage('AI estimates updated with latest data', 'success');
    }
  }, [fetchAiInsights, isLoadingAi, showMessage]);

  const allStrategies = useMemo(() => {
    if (!safeAsset) return [];
    return Object.entries(enhancedRStrategies).map(([key, strategy]) => {
      const implementationCost = safeAsset.currentValue * strategy.costFactor;
      const recoveredValue = safeAsset.currentValue * strategy.recoveryRate;
      const netValue = recoveredValue - implementationCost;
      const roi = implementationCost > 0 ? ((recoveredValue - implementationCost) / implementationCost) * 100 : 0;
      return { key, ...strategy, implementationCost, recoveredValue, netValue, roi, riskLevel: calculateRiskLevel(key) };
    }).sort((a, b) => b.roi - a.roi);
  }, [safeAsset, enhancedRStrategies]);

  // Compose a single result object for TriageResultCard from real/AI data
  const triageCardResult = useMemo(() => {
    const selectedKey = (triageResult?.suggestedStrategy) || (aiStrategyRecommendations?.recommended_r_strategy) || (safeAsset?.current_strategy) || null;
    if (!selectedKey) return null;
    const details = allStrategies.find(s => s.key === selectedKey) || null;

    // Confidence: prefer backend AI recommendation confidence (0-1 → 0-100)
    const aiConfidence01 = (aiStrategyRecommendations && typeof aiStrategyRecommendations.confidence === 'number')
      ? aiStrategyRecommendations.confidence
      : (aiAnalysis && typeof aiAnalysis.confidence === 'number' ? aiAnalysis.confidence : null);
    const confidencePct = aiConfidence01 != null ? Math.max(0, Math.min(100, aiConfidence01 * 100)) : null;

    // Map numeric risk level (0-1) to label
    const toRiskLabel = (lvl) => {
      if (lvl == null) return null;
      if (lvl >= 0.66) return 'High';
      if (lvl >= 0.33) return 'Medium';
      return 'Low';
    };

    const risk = details ? toRiskLabel(details.riskLevel) : null;

    // Time and cost from strategy details when available
    const timeDays = details?.timeToImplement != null ? `${details.timeToImplement} days` : null;
    const costValue = details?.implementationCost != null ? `$${Number(details.implementationCost).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : null;

    const description = (triageResult?.rationale) || (aiStrategyRecommendations?.reasoning) || null;

    return {
      strategy: selectedKey,
      confidence: confidencePct != null ? confidencePct : null,
      impact: null, // optional: leave null unless we compute a reliable 0–5 scale from AI
      cost: costValue,
      time: timeDays,
      description,
      risk,
    };
  }, [triageResult, aiStrategyRecommendations, aiAnalysis, allStrategies, safeAsset?.current_strategy]);

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down': return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const renderSimulationModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-800"><Zap className="w-5 h-5 inline-block mr-2 text-yellow-500" />Strategy Simulation: {simulationResults?.strategy}</h3>
          <button onClick={() => setShowSimulationModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
        </div>
        {simulationResults && (
          <div className="p-6 space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3">Simulation Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-3 rounded-lg shadow">
                  <div className="text-sm text-gray-500">Implementation Cost</div>
                  <div className="text-xl font-bold">${simulationResults.implementationCost.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow">
                  <div className="text-sm text-gray-500">Recovered Value</div>
                  <div className="text-xl font-bold text-green-600">${simulationResults.recoveredValue.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow">
                  <div className="text-sm text-gray-500">Net Value</div>
                  <div className={`text-xl font-bold ${simulationResults.netValue >= 0 ? 'text-green-600' : 'text-red-600'}`}>${Math.abs(simulationResults.netValue).toLocaleString(undefined, {maximumFractionDigits: 2})}{simulationResults.netValue < 0 ? ' (Loss)' : ' (Gain)'}</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-3 rounded-lg shadow">
                  <div className="text-sm text-gray-500">ROI</div>
                  <div className={`text-xl font-bold ${simulationResults.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>{simulationResults.roi.toFixed(1)}%</div>
                </div>
                <div className="bg-white p-3 rounded-lg shadow">
                  <div className="text-sm text-gray-500">Implementation Time</div>
                  <div className="text-xl font-bold">{simulationResults.timeToImplement} days</div>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Material Impact</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Material</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Current Value</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Future Value</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {simulationResults.materialValues.map((mat, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 whitespace-nowrap"><div className="flex items-center"><span className="font-medium">{mat.name}</span>{getTrendIcon(materialMarketData[mat.name]?.trend)}</div></td>
                        <td className="px-4 py-2 text-right">${mat.value.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                        <td className="px-4 py-2 text-right">${mat.futureValue.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                        <td className={`px-4 py-2 text-right ${mat.priceChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{mat.priceChangePercent.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center"><AlertTriangle className="w-5 h-5 mr-2 text-yellow-500" />Risk Assessment</h4>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2"><div className={`h-2.5 rounded-full ${simulationResults.riskLevel > 70 ? 'bg-red-500' : simulationResults.riskLevel > 40 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${simulationResults.riskLevel}%` }}></div></div>
              <p className="text-sm text-gray-700 mb-4">{simulationResults.riskLevel > 70 ? 'High Risk' : simulationResults.riskLevel > 40 ? 'Medium Risk' : 'Low Risk'}</p>
              <button className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center transition" onClick={() => { const chosen = simulationResults?.strategy || triageResult?.suggestedStrategy; if (chosen) { onSaveStrategy(safeAsset.id, chosen); } setShowSimulationModal(false); }}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Apply This Strategy
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderRiskSimulationModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-800"><AlertTriangle className="w-5 h-5 inline-block mr-2 text-yellow-500" />Risk Simulation</h3>
          <button onClick={() => setShowRiskSimulationModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">How Risk is Determined</h4>
            <div className="text-sm text-gray-700 space-y-1">
              <p><strong>Base Risk Calculation:</strong></p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>Condition Factor:</strong> (5 - condition rating) × 8 - Penalizes lower asset condition</li>
                <li><strong>Depreciation Factor:</strong> (current value / initial value) × 20 - Accounts for asset depreciation</li>
                <li><strong>Cost Impact:</strong> Financial cost percentage × 1 - Direct cost increase</li>
                <li><strong>Time Factor:</strong> (days / 180) × 10 - Risk increases with longer timeframes</li>
                <li><strong>Market Trend:</strong> +15 for increasing costs, -5 for decreasing costs</li>
              </ul>
              <p className="mt-2"><strong>Risk Levels:</strong> Low (0-25), Medium (26-50), High (51-75), Extreme (76-100)</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-600">Cost Impact %</div>
              <input type="number" name="financialCostPercentage" value={riskInputs.financialCostPercentage} onChange={handleRiskInputChange} className="mt-1 w-full border rounded p-2" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Days</div>
              <input type="number" name="days" value={riskInputs.days} onChange={handleRiskInputChange} className="mt-1 w-full border rounded p-2" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Relative Cost</div>
              <select name="relativeCostChange" value={riskInputs.relativeCostChange} onChange={handleRiskInputChange} className="mt-1 w-full border rounded p-2">
                <option value="stable">Stable</option>
                <option value="increase">Increase</option>
                <option value="decrease">Decrease</option>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <button onClick={runRiskSimulation} disabled={isLoadingRisk} className={`bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg ${isLoadingRisk ? 'opacity-60 cursor-not-allowed' : 'hover:bg-yellow-700'}`}>
              {isLoadingRisk ? 'Running...' : 'Run Risk Simulation'}
            </button>
            {riskResult && (
              <div className="text-sm">
                <div className={`font-bold ${riskResult.colorClass}`}>{riskResult.level}</div>
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div className={`h-2 rounded-full ${riskResult.badgeClass}`} style={{ width: `${riskResult.score}%` }}></div>
                </div>
              </div>
            )}
          </div>
          {riskResult && <p className="mt-3 text-sm text-gray-700">{riskResult.analysis}</p>}
        </div>
      </div>
    </div>
  );

  const renderAiEstimateStatus = () => {
    if (!aiSuggestions) return null;
    const lastUpdated = aiSuggestions.lastFetched || aiSuggestions.lastUpdated;
    const isEstimate = aiSuggestions.isEstimate !== false;
    const isLoading = aiSuggestions.isLoading;
    const confidence = aiSuggestions.confidence || 0.8;
    let confidenceColor = 'text-green-600';
    if (confidence < 0.7) confidenceColor = 'text-yellow-600';
    if (confidence < 0.5) confidenceColor = 'text-red-600';
    return (
      <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center">
            <BarChart className="w-4 h-4 mr-2 text-blue-600" />
            <span className="font-medium text-sm">AI-Powered Estimates</span>
            {isLoading && (<span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center"><Loader className="w-3 h-3 mr-1 animate-spin" />Updating...</span>)}
            {!isLoading && isEstimate && (<span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">Local Estimation</span>)}
          </div>
          <button onClick={handleRefreshAiInsights} disabled={isLoading} className={`flex items-center text-sm ${isLoading ? 'text-gray-400' : 'text-blue-600 hover:text-blue-800'}`}>
            <RefreshCw className={`w-4 h-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <div className="text-xs text-gray-600">
          {lastUpdated && (<div className="mb-1">Last updated: {new Date(lastUpdated).toLocaleString()}</div>)}
          <div className="flex items-center">
            <span>Confidence: </span>
            <span className={`ml-1 font-medium ${confidenceColor}`}>{Math.round(confidence * 100)}%</span>
            <div className="ml-2 w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className={`h-full ${confidence > 0.7 ? 'bg-green-500' : confidence > 0.4 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${Math.round(confidence * 100)}%` }} /></div>
          </div>
          {aiSuggestions.error && (<div className="mt-1 text-red-500 text-xs"><AlertOctagon className="inline w-3 h-3 mr-1" />{aiSuggestions.error}</div>)}
          {aiSuggestions.warning && (<div className="mt-1 text-yellow-600 text-xs"><AlertTriangle className="inline w-3 h-3 mr-1" />{aiSuggestions.warning}</div>)}
        </div>
      </div>
    );
  };

  if (isLoading || !safeAsset) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-cyan-600" />
            <p className="text-gray-600">Loading asset data...</p>
          </div>
        </div>
      </div>
    );
  }

  // PDF export helpers (continued - hooks declared at top)
  const loadScript = (src) => new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) { existing.onload = resolve; return resolve(); }
    const s = document.createElement('script'); s.src = src; s.async = true; s.onload = resolve; s.onerror = reject; document.body.appendChild(s);
  });
  const handleDownloadPdf = async () => {
    try {
      setIsExportingPdf(true);
      // Load libs lazily from CDN to avoid bundling
      await loadScript('https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js');
      await loadScript('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js');
      // @ts-ignore
      const html2canvas = window.html2canvas; // global from CDN
      // @ts-ignore
      const { jsPDF } = window.jspdf;
      const node = exportRef.current;
      if (!node) throw new Error('Nothing to export');
      const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth; // fit width
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let position = 0;
      let heightLeft = imgHeight;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      }
      pdf.save(`triage_${safeAsset.id}.pdf`);
    } catch (err) {
      console.error('Client PDF export failed, falling back to backend:', err);
      try {
        // Fallback to backend PDF
        const resp = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/reports/asset/${safeAsset.id}.pdf`);
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `asset_${safeAsset.id}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      } catch (e2) {
        alert('Failed to generate PDF');
      }
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {showSimulationModal && renderSimulationModal()}
      {showRiskSimulationModal && renderRiskSimulationModal()}
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="flex items-center text-gray-700 hover:text-cyan-600 transition">
          <ArrowLeft className="w-5 h-5 mr-2" /> Back to Asset Manager</button>
        <button onClick={handleDownloadPdf} disabled={isExportingPdf} className={`inline-flex items-center px-3 py-2 rounded-md text-white ${isExportingPdf ? 'bg-gray-300' : 'bg-gray-700 hover:bg-gray-800'}`}>
          <FileText className="w-4 h-4 mr-1" /> {isExportingPdf ? 'Preparing PDF…' : 'Download PDF'}
        </button>
      </div>
      <h2 className="text-3xl font-extrabold text-gray-800 mb-6 flex items-center border-b pb-2">
        <HardHat className="w-7 h-7 mr-3 text-yellow-600" />Circular Triage & Risk Analysis: <span className="text-cyan-600 ml-2">{safeAsset.name} ({safeAsset.id})</span></h2>
      <div ref={exportRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {renderAiEstimateStatus()}
          <div className="bg-white p-6 rounded-xl shadow-xl space-y-4 border-l-4 border-indigo-500">
            <h3 className="text-xl font-bold text-gray-800 border-b pb-2 mb-3 flex items-center"><FileText className="w-5 h-5 mr-2" />
              Current Asset Metrics</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-lg font-medium">
                <div className="flex items-center">
                  <span>Condition Rating:</span>{aiSuggestions?.conditionRating && (<span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">AI Estimated</span>)}</div>
                <span className="flex items-center text-gray-800">{getConditionIcon(Number(aiSuggestions?.conditionRating ?? safeAsset.conditionRating))}{Number(aiSuggestions?.conditionRating ?? safeAsset.conditionRating).toFixed(1)}/5.0</span>
              </div>
              <div className="flex justify-between items-center text-lg font-medium">
                <div className="flex items-center"><span>Circularity Score:</span>{aiSuggestions?.circularityScore && (<span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">AI Estimated</span>)}</div>
                <span className="text-cyan-600 font-bold">{Number(aiSuggestions?.circularityScore ?? safeAsset.circularityScore).toFixed(1)}%</span>
              </div>
            </div>
            <div className="mt-4 space-y-4">
              <div className="flex justify-between"><div className="flex items-center"><span className="text-gray-600">Initial Asset Value</span><
                span className="ml-2 text-xs text-gray-400" title="Current market value of the asset">
                <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></span></div>
                  <span className="font-medium">${Number(safeAsset.initialValue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>

              <div className="mt-4 space-y-4">
              <div className="flex justify-between"><div className="flex items-center"><span className="text-gray-600">Current Asset Value</span><
                span className="ml-2 text-xs text-gray-400" title="Current market value of the asset">
                <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></span></div>
                  <span className="font-medium">
                  ${Number(safeAsset.currentValue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </span>
                  </div>
                  </div>



              <div className="flex justify-between"><div className="flex items-center"><span className="text-gray-600">AI Estimated Circularity Score</span><span className="ml-2 text-xs text-gray-400" title="AI-calculated circularity potential"><svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg></span></div><span className="font-medium text-cyan-600">{aiAnalysis?.circularity_score ? `${aiAnalysis.circularity_score.toFixed(1)}%` : 'Not analyzed'}</span></div>
              <div className="flex justify-between"><div className="flex items-center"><span className="text-gray-600">AI Estimated Circular Potential</span><span className="ml-2 text-xs text-gray-400" title="AI-calculated recoverable value"><svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></span></div><span className="font-medium text-green-600">{aiAnalysis?.circular_potential ? `$${aiAnalysis.circular_potential.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : 'Not analyzed'}</span></div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-semibold text-gray-800">
                    AI Analysis & Recommendations</h4>
                    <button onClick={handleRefreshAiInsights} className="text-blue-600 hover:text-blue-800 flex items-center text-sm">
                      <RefreshCw className={`w-4 h-4 mr-1 ${isLoadingAi ? 'animate-spin' : ''}`} />Refresh
                    </button>
                </div>
              </div>

              {/* AI Analysis Results */}
              {aiAnalysis && (
                <div className="mt-4 p-4 bg-cyan-50 rounded-lg border border-cyan-100">
                  <div className="flex items-center text-cyan-700 mb-2">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <span className="font-semibold">AI Asset Analysis</span>
                    {isFallbackAnalysis && (
                      <span
                        className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full border border-yellow-200"
                        title="Heuristic fallback used due to unavailable or slow AI."
                      >
                        Fallback Analysis
                      </span>
                    )}
                    <span
                      className="ml-auto text-xs bg-cyan-100 text-cyan-800 px-2 py-1 rounded-full"
                      title="Model-reported confidence or conservative fallback estimate"
                    >
                      Confidence: {Math.round((aiAnalysis.confidence || 0.8) * 100)}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{aiAnalysis.reasoning || 'AI-powered circularity assessment'}</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-white p-2 rounded border" title="AI-estimated or heuristic fallback circularity score (0-100%)">
                      <div className="text-gray-500 text-xs">Circularity Score</div>
                      <div className="font-medium text-cyan-600">{aiAnalysis.circularity_score?.toFixed(1)}%</div>
                    </div>
                    <div className="bg-white p-2 rounded border" title="Estimated recoverable value given the current score and category caps">
                      <div className="text-gray-500 text-xs">Circular Potential</div>
                      <div className="font-medium text-green-600">${aiAnalysis.circular_potential?.toLocaleString()}</div>
                    </div>
                  </div>

                  {/* Parsed materials used for analysis/fallback */}
                  {Array.isArray(parsedMaterialsForAI) && parsedMaterialsForAI.length > 0 && (
                    <div className="mt-3" title="Materials parsed from the asset record; used to weight bonuses/penalties in fallback analysis">
                      <div className="text-xs text-gray-600 mb-1">Parsed Materials (for analysis)</div>
                      <div className="flex flex-wrap gap-2">
                        {parsedMaterialsForAI.slice(0, 12).map((m, idx) => (
                          <span key={idx} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full border border-gray-200">
                            {m.name}{m.percentage != null ? ` (${Number(m.percentage).toFixed(0)}%)` : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}


              {/* AI Strategy Recommendations */}
              {aiStrategyRecommendations && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-100">
                  <div className="flex items-center text-green-700 mb-2">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="font-semibold">AI Strategy Recommendations</span>
                    <span className="ml-auto text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      Confidence: {Math.round((aiStrategyRecommendations.confidence || 0.8) * 100)}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{aiStrategyRecommendations.reasoning || 'AI-powered strategy recommendations'}</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-white p-2 rounded border">
                      <div className="text-gray-500 text-xs">Recommended R Strategy</div>
                      <div className="font-medium text-blue-600">{aiStrategyRecommendations.recommended_r_strategy || 'N/A'}</div>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <div className="text-gray-500 text-xs">End-of-Life Strategy</div>
                      <div className="font-medium text-green-600 text-xs">{aiStrategyRecommendations.recommended_end_of_life_strategy || 'N/A'}</div>
                    </div>
                  </div>
                  {aiStrategyRecommendations.alternatives && aiStrategyRecommendations.alternatives.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-green-100">
                      <div className="text-xs font-medium text-green-700 mb-1">Alternative Strategies:</div>
                      <div className="space-y-1">
                        {aiStrategyRecommendations.alternatives.map((alt, idx) => (
                          <div key={idx} className="text-xs text-gray-600">
                            <span className="font-medium">{alt.strategy}:</span> {alt.reasoning}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Digital Product Passport */}
          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-emerald-500">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Digital Product Passport</h3>
            <p className="text-xs text-gray-600 -mt-2 mb-3">A concise, DPP-like summary built from the asset’s own fields (identity, lifecycle, materials, strategies, performance, value). Use "Generate Passport" to save the current snapshot to the asset record.</p>
            {isLoadingPassport ? (
              <div className="text-sm text-gray-500 flex items-center"><Loader className="w-4 h-4 mr-2 animate-spin" /> Loading passport...</div>
            ) : passportError ? (
              <div className="text-sm text-red-600">{passportError}</div>
            ) : passport ? (
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-gray-500">Type:</span> <span className="font-medium">{passport.type || '—'}</span></div>
                  <div><span className="text-gray-500">Age:</span> <span className="font-medium">{passport.lifecycle?.age_months ?? '—'} mo</span></div>
                  <div><span className="text-gray-500">Life Expectancy:</span> <span className="font-medium">{passport.lifecycle?.life_expectancy_months ?? '—'} mo</span></div>
                  <div><span className="text-gray-500">Current Strategy:</span> <span className="font-medium">{passport.strategies?.current || '—'}</span></div>
                </div>
                <div className="mt-2">
                  <div className="text-xs text-gray-600 mb-1">Materials</div>
                  {passport.materials && passport.materials.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {passport.materials.slice(0,6).map((m, idx) => (
                        <span key={idx} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full border border-gray-200">{m.name} {m.percentage != null ? `(${m.percentage}%)` : ''}</span>
                      ))}
                    </div>
                  ) : (<div className="text-sm text-gray-500">No materials listed</div>)}
                </div>
                <details className="mt-3">
                  <summary className="text-xs text-cyan-700 cursor-pointer">View raw passport JSON</summary>
                  <pre className="mt-2 p-2 bg-gray-50 rounded border text-xs overflow-x-auto">{JSON.stringify(passport, null, 2)}</pre>
                </details>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No passport available.</div>
            )}
            <div className="mt-3">
              <button onClick={async () => { if (!safeAsset) return; setIsGeneratingPassport(true); try { const res = await generateAssetPassport(safeAsset.id); setPassport(res.passport || passport); showMessage && showMessage('Passport generated', 'success'); } catch (e) { setPassportError(e?.message || 'Failed to generate passport'); showMessage && showMessage('Failed to generate passport', 'error'); } finally { setIsGeneratingPassport(false); } }} className={`px-3 py-2 rounded-lg text-white text-sm ${isGeneratingPassport ? 'bg-gray-400' : 'bg-emerald-600 hover:bg-emerald-700'}`} disabled={isGeneratingPassport}>
                {isGeneratingPassport ? 'Generating...' : 'Generate Passport'}
              </button>
            </div>
          </div>

          {/* Material Circularity Indicator */}
          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-indigo-500">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Material Circularity Indicator (MCI)</h3>
            {isLoadingMci ? (
              <div className="text-sm text-gray-500 flex items-center"><Loader className="w-4 h-4 mr-2 animate-spin" /> Calculating MCI...</div>
            ) : mciError ? (
              <div className="text-sm text-red-600">{mciError}</div>
            ) : mci ? (
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Score</span>
                  <span className="text-lg font-bold text-indigo-600">{Number(mci.mci || 0).toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${Math.max(0, Math.min(100, Number(mci.mci || 0)))}%` }} />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                  <div className="bg-gray-50 p-2 rounded border"><div className="text-gray-500">Diversity</div><div className="font-medium">{Number(mci.diversity || 0).toFixed(2)}</div></div>
                  <div className="bg-gray-50 p-2 rounded border"><div className="text-gray-500">Life Ratio</div><div className="font-medium">{Number(mci.life_ratio || 0).toFixed(2)}</div></div>
                  <div className="bg-gray-50 p-2 rounded border"><div className="text-gray-500">Recycled Proxy</div><div className="font-medium">{Number(mci.recycled_proxy || 0).toFixed(2)}</div></div>
                </div>
              </div>
            ) : (<div className="text-sm text-gray-500">No MCI data.</div>)}
          </div>

          {/* LCA-light */}
          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-fuchsia-500">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Environmental Impact (LCA‑light)</h3>
            {isLoadingLca ? (
              <div className="text-sm text-gray-500 flex items-center"><Loader className="w-4 h-4 mr-2 animate-spin" /> Estimating footprint...</div>
            ) : lcaError ? (
              <div className="text-sm text-red-600">{lcaError}</div>
            ) : lca ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="bg-white p-3 rounded-lg shadow border"><div className="text-gray-500 text-xs">CO₂</div><div className="text-lg font-bold text-fuchsia-600">{Number(lca.co2_kg || 0).toLocaleString()} kg</div></div>
                <div className="bg-white p-3 rounded-lg shadow border"><div className="text-gray-500 text-xs">Energy</div><div className="text-lg font-bold text-fuchsia-600">{Number(lca.energy_kwh || 0).toLocaleString()} kWh</div></div>
                <div className="bg-white p-3 rounded-lg shadow border"><div className="text-gray-500 text-xs">Water</div><div className="text-lg font-bold text-fuchsia-600">{Number(lca.water_l || 0).toLocaleString()} L</div></div>
              </div>
            ) : (<div className="text-sm text-gray-500">No LCA estimate.</div>)}
          </div>

          {/* Repairability & RUL */}
          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-orange-500">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Repairability & Remaining Useful Life</h3>
            {(isLoadingRepairability || isLoadingRul) ? (
              <div className="text-sm text-gray-500 flex items-center"><Loader className="w-4 h-4 mr-2 animate-spin" /> Loading...</div>
            ) : (repairabilityError || rulError) ? (
              <div className="text-sm text-red-600">{repairabilityError || rulError}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="bg-white p-3 rounded-lg shadow border"><div className="text-gray-500 text-xs">Repairability</div><div className="text-lg font-bold text-orange-600">{Number(repairability?.repairability || 0).toFixed(1)} / 10</div></div>
                <div className="bg-white p-3 rounded-lg shadow border"><div className="text-gray-500 text-xs">RUL</div><div className="text-lg font-bold text-orange-600">{Number(rul?.rul_months || 0).toFixed(1)} mo</div></div>
                <div className="bg-white p-3 rounded-lg shadow border"><div className="text-gray-500 text-xs">Usage Hours</div><div className="text-lg font-bold text-orange-600">{Number(rul?.usage_hours || 0).toLocaleString()}</div></div>
                {Array.isArray(repairability?.tips) && repairability.tips.length > 0 && (
                  <div className="md:col-span-3">
                    <div className="text-xs text-gray-600 mb-1">Tips</div>
                    <ul className="list-disc ml-5 text-xs text-gray-700 space-y-1">
                      {repairability.tips.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Compliance (SVHC pre-check) */}
          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-red-500">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Compliance (SVHC Pre‑Check)</h3>
            {isLoadingCompliance ? (
              <div className="text-sm text-gray-500 flex items-center"><Loader className="w-4 h-4 mr-2 animate-spin" /> Checking materials…</div>
            ) : complianceError ? (
              <div className="text-sm text-red-600">{complianceError}</div>
            ) : (
              <div className="text-sm">
                {Array.isArray(complianceFlags) && complianceFlags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {complianceFlags.map((f, idx) => (
                      <span key={idx} className="px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 border border-red-200" title="SVHC match">
                        {f.name} — {f.flag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">No SVHC flags detected</span>
                )}
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-cyan-500">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-800">Material Composition & Market</h3>
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleRefreshMarket} className="text-xs px-2 py-1 rounded border text-gray-700 hover:bg-gray-50">Refresh Market</button>
                <button type="button" onClick={handleReestimateMaterials} disabled={isEstimatingMaterials} className={`text-xs px-2 py-1 rounded ${isEstimatingMaterials ? 'bg-gray-300 text-white' : 'bg-purple-600 text-white hover:bg-purple-700'}`}>{isEstimatingMaterials ? 'Estimating…' : 'Re‑estimate with AI'}</button>
              </div>
            </div>
            {materialBreakdown && materialBreakdown.length > 0 ? (
              <div className="space-y-3">
                {materialBreakdown.map((m, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="w-40 text-sm text-gray-700 flex items-center">
                      <span className="font-medium" title={m.name}>{m.name}</span>
                      <span className="ml-1" aria-hidden>{getTrendIcon(m.trend)}</span>
                      {m.isCritical && (
                        <span title="EU Critical Raw Material: supply concentration & geopolitical risk"
                              className="ml-2 px-1.5 py-0.5 text-[10px] rounded-full bg-red-50 text-red-700 border border-red-200">CRITICAL</span>
                      )}
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${Math.max(0, Math.min(100, Math.round(m.percentage)))}%` }} />
                    </div>
                    <span className="w-12 text-right text-sm font-medium">{Math.round(m.percentage)}%</span>
                    <span className="w-28 text-right text-xs text-gray-700" title="Spot price">
                      {m.currentPrice ? `$${Number(m.currentPrice).toFixed(2)}/kg` : '—'}
                    </span>
                    <span className="w-20 text-right text-[11px] text-gray-500" title="Historical volatility">
                      {Number.isFinite(m.volatility) ? `${Math.round(m.volatility*100)}% vol` : '—'}
                    </span>
                  </div>
                ))}
                <div className="text-xs text-gray-500 pt-2 border-t">
                  Total: {Math.round(materialBreakdown.reduce((s,m)=> s + (Number(m.percentage)||0), 0))}%
                </div>
              </div>
            ) : (<p className="text-gray-500">No material data available.</p>)}
          </div>

          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-indigo-500">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Strategy Comparison</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50"><tr><th className="px-4 py-2 text-left font-medium text-gray-600">Strategy</th><th className="px-4 py-2 text-right font-medium text-gray-600">Cost</th><th className="px-4 py-2 text-right font-medium text-gray-600">Recovered</th><th className="px-4 py-2 text-right font-medium text-gray-600">Net</th><th className="px-4 py-2 text-right font-medium text-gray-600">ROI</th><th className="px-4 py-2 text-right font-medium text-gray-600">Risk</th></tr></thead>
                <tbody className="divide-y divide-gray-200">
                  {allStrategies.map((s) => (
                    <tr key={s.key} className="hover:bg-gray-50"><td className="px-4 py-2">{s.name || s.key}</td><td className="px-4 py-2 text-right">${s.implementationCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td><td className="px-4 py-2 text-right">${s.recoveredValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td><td className={`px-4 py-2 text-right ${s.netValue >= 0 ? 'text-green-600' : 'text-red-600'}`}>${Math.abs(s.netValue).toLocaleString(undefined, { maximumFractionDigits: 2 })}{s.netValue < 0 ? ' (Loss)' : ''}</td><td className={`px-4 py-2 text-right ${s.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>{s.roi.toFixed(1)}%</td><td className="px-4 py-2 text-right">{Math.round(s.riskLevel * 100) / 100}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>

        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-green-500">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Strategy Recommendation</h3>
            <TriageResultCard
              // Display real data only; no hard-coded values
              result={triageCardResult || {}}
              hasTriageResult={!!triageCardResult}
              isProcessing={isLoadingTriage || isLoadingAi}
              strategyKey={triageCardResult?.strategy || triageResult?.suggestedStrategy || aiStrategyRecommendations?.recommended_r_strategy || safeAsset?.current_strategy}
              simulateGeminiTriage={simulateGeminiTriage}
              isSelected={Boolean((triageCardResult?.strategy || safeAsset?.current_strategy) && (triageCardResult?.strategy === safeAsset?.current_strategy))}
              onApply={(res) => {
                const key = (res && res.strategy) || (triageCardResult?.strategy);
                if (!key) return;
                onSaveStrategy && onSaveStrategy(safeAsset.id, key);
              }}
            />

            {/* AI Strategy Recommendations Display */}
            {triageResult && aiStrategyRecommendations && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">AI Recommendations Available</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">R Strategy (AI):</span>
                    <span className="ml-1 font-medium text-blue-600">{aiStrategyRecommendations.recommended_r_strategy}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">End-of-Life (AI):</span>
                    <span className="ml-1 font-medium text-green-600 text-xs">{aiStrategyRecommendations.recommended_end_of_life_strategy}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 px-4 flex flex-col items-stretc space-y-2 space-2">
              <button onClick={runTriage} disabled={isLoadingTriage} 
               className={`bg-emerald-600 text-white font-semibold py-2 px-4 rounded-lg ${isLoadingTriage ? 'opacity-60 cursor-not-allowed' : 'hover:bg-emerald-700'}`}>
                {isLoadingTriage ? 'Processing...' : 'Run Strategy Triage'}
              </button>
              <button onClick={() => fetchAiInsights(true)} disabled={isLoadingAi} 
               className={`bg-purple-600 text-white font-semibold py-2 px-4 rounded-lg ${isLoadingAi ? 'opacity-60 cursor-not-allowed' : 'hover:bg-purple-700'}`}>
                {isLoadingAi ? 'Analyzing...' : 'Run AI Analysis'}
              </button>
              <button onClick={() => setShowRiskSimulationModal(true)} 
                className="bg-indigo-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-indigo-700">Open Simulation</button>
            {/* </div> */}

            {/* <div className="mt-4 flex space-x-2 items-center"> */}
              <button onClick={() => onSaveAIRecommendations && onSaveAIRecommendations(safeAsset.id)} className="bg-green-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-green-700">Save AI Recommendations</button>
              <button onClick={() => (triageResult?.suggestedStrategy || aiStrategyRecommendations?.recommended_r_strategy) && onSaveStrategy(safeAsset.id, triageResult?.suggestedStrategy || aiStrategyRecommendations.recommended_r_strategy)} disabled={!triageResult?.suggestedStrategy && !aiStrategyRecommendations?.recommended_r_strategy} className="bg-cyan-600 text-white font-semibold py-2 px-4 rounded-lg disabled:opacity-50 hover:bg-cyan-700">Save Strategy</button>
            </div>


          </div>


          <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-cyan-500">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Execution Timeline</h3>
            <RStrategyTimeline asset={timelineAsset} hasTriageResult={!!triageResult} strategyKey={triageResult?.suggestedStrategy} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetTriagePage;
